import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Category,
  SubdirectoryArrowRight
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { appConfig } from '../../config/app';
import ImageUpload from '../../components/common/ImageUpload';

const CategoryManagement = () => {
  const { actions } = useApp();
  
  // State management
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal states
  const [categoryModal, setCategoryModal] = useState({
    open: false,
    mode: 'add', // 'add' or 'edit'
    data: { id: '', name: '', description: '', sortOrder: 1, image: '', categoryId: '' }
  });
  
  const [subCategoryModal, setSubCategoryModal] = useState({
    open: false,
    mode: 'add', // 'add' or 'edit'
    data: { id: '', name: '', description: '', parentCategoryId: '', sortOrder: 1 }
  });
  
  // Delete confirmation dialogs
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: '', // 'category' or 'sub-category'
    item: null
  });

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${appConfig.api.baseURL}/categories`);
      if (response.data.success) {
        setCategories(response.data.data);
      } else {
        setError('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to fetch categories');
    }
  };

  // Fetch sub-categories
  const fetchSubCategories = async () => {
    try {
      const response = await axios.get(`${appConfig.api.baseURL}/categories/sub-categories/list`);
      if (response.data.success) {
        setSubCategories(response.data.data);
      } else {
        setError('Failed to fetch sub-categories');
      }
    } catch (error) {
      console.error('Error fetching sub-categories:', error);
      setError('Failed to fetch sub-categories');
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCategories(), fetchSubCategories()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Handle category form submission
  const handleCategorySubmit = async () => {
    try {
      const { mode, data } = categoryModal;
      const payload = {
        name: data.name.trim(),
        description: data.description.trim(),
        sortOrder: parseInt(data.sortOrder) || 1,
        image: data.image,
        id: data.categoryId.trim()
      };

      let response;
      if (mode === 'add') {
        response = await axios.post(`${appConfig.api.baseURL}/categories`, payload);
      } else {
        response = await axios.put(`${appConfig.api.baseURL}/categories/${data.documentId}`, payload);
      }

      if (response.data.success) {
        actions.showSnackbar(
          `Category ${mode === 'add' ? 'created' : 'updated'} successfully`, 
          'success'
        );
        fetchCategories();
        setCategoryModal({ open: false, mode: 'add', data: { id: '', name: '', description: '' } });
      } else {
        setError(response.data.message || `Failed to ${mode} category`);
      }
    } catch (error) {
      console.error('Error submitting category:', error);
      setError(error.response?.data?.message || `Failed to ${categoryModal.mode} category`);
    }
  };

  // Handle sub-category form submission
  const handleSubCategorySubmit = async () => {
    try {
      const { mode, data } = subCategoryModal;
      const payload = {
        name: data.name.trim(),
        description: data.description.trim(),
        parentCategoryId: data.parentCategoryId,
        sortOrder: parseInt(data.sortOrder) || 1
      };

      let response;
      if (mode === 'add') {
        response = await axios.post(`${appConfig.api.baseURL}/categories/sub-categories`, payload);
      } else {
        response = await axios.put(`${appConfig.api.baseURL}/categories/sub-categories/${data.documentId}`, payload);
      }

      if (response.data.success) {
        actions.showSnackbar(
          `Sub-category ${mode === 'add' ? 'created' : 'updated'} successfully`, 
          'success'
        );
        fetchSubCategories();
        setSubCategoryModal({ 
          open: false, 
          mode: 'add', 
          data: { id: '', name: '', description: '', parentCategoryId: '', sortOrder: 1 } 
        });
      } else {
        setError(response.data.message || `Failed to ${mode} sub-category`);
      }
    } catch (error) {
      console.error('Error submitting sub-category:', error);
      setError(error.response?.data?.message || `Failed to ${subCategoryModal.mode} sub-category`);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      const { type, item } = deleteDialog;
      let response;
      
      if (type === 'category') {
        response = await axios.delete(`${appConfig.api.baseURL}/categories/${item.documentId}`);
      } else {
        response = await axios.delete(`${appConfig.api.baseURL}/categories/sub-categories/${item.documentId}`);
      }

      if (response.data.success) {
        actions.showSnackbar(`${type} deleted successfully`, 'success');
        if (type === 'category') {
          fetchCategories();
          fetchSubCategories(); // Refresh sub-categories in case any were affected
        } else {
          fetchSubCategories();
        }
        setDeleteDialog({ open: false, type: '', item: null });
      } else {
        setError(response.data.message || `Failed to delete ${type}`);
      }
    } catch (error) {
      console.error('Error deleting:', error);
      setError(error.response?.data?.message || `Failed to delete ${deleteDialog.type}`);
    }
  };

  // Open category modal for editing
  const openCategoryEdit = (category) => {
    setCategoryModal({
      open: true,
      mode: 'edit',
      data: { 
        ...category,
        sortOrder: category.sortOrder || 1,
        image: category.image || '',
        categoryId: category.id || '',
        documentId: category.documentId
      }
    });
  };

  // Open sub-category modal for editing
  const openSubCategoryEdit = (subCategory) => {
    setSubCategoryModal({
      open: true,
      mode: 'edit',
      data: { 
        ...subCategory,
        sortOrder: subCategory.sortOrder || 1,
        documentId: subCategory.documentId
      }
    });
  };

  // Validation
  const isCategoryFormValid = () => {
    return categoryModal.data.name.trim().length > 0;
  };

  const isSubCategoryFormValid = () => {
    return subCategoryModal.data.name.trim().length > 0 && 
           subCategoryModal.data.parentCategoryId.length > 0;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Category Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage product categories and sub-categories
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Categories
              </Typography>
              <Typography variant="h4">
                {categories.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Sub-Categories
              </Typography>
              <Typography variant="h4">
                {subCategories.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Categories Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Categories"
          action={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCategoryModal({ 
                open: true, 
                mode: 'add', 
                data: { id: '', name: '', description: '' } 
              })}
            >
              Add Category
            </Button>
          }
        />
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Sort Order</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Sub-Categories</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Loading categories...
                    </TableCell>
                  </TableRow>
                ) : categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No categories found
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((category) => {
                    const subCategoryCount = subCategories.filter(
                      sub => sub.parentCategoryId === category.id
                    ).length;

                    return (
                      <TableRow key={category.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Category color="primary" />
                            <Typography variant="subtitle2" fontWeight="bold">
                              {category.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {category.description || 'No description'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {category.id || 'No ID'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {category.sortOrder || 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {category.image ? (
                            <Box
                              component="img"
                              src={category.image}
                              alt={category.name}
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No image
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${subCategoryCount} sub-categories`} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit Category">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => openCategoryEdit(category)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Category">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => setDeleteDialog({ 
                                open: true, 
                                type: 'category', 
                                item: category 
                              })}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Sub-Categories Section */}
      <Card>
        <CardHeader
          title="Sub-Categories"
          action={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setSubCategoryModal({ 
                open: true, 
                mode: 'add', 
                data: { id: '', name: '', description: '', parentCategoryId: '', sortOrder: 1 } 
              })}
              disabled={categories.length === 0}
            >
              Add Sub-Category
            </Button>
          }
        />
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Parent Category</TableCell>
                  <TableCell>Sort Order</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Loading sub-categories...
                    </TableCell>
                  </TableRow>
                ) : subCategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No sub-categories found
                    </TableCell>
                  </TableRow>
                ) : (
                  subCategories.map((subCategory) => (
                    <TableRow key={subCategory.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SubdirectoryArrowRight color="secondary" />
                          <Typography variant="subtitle2" fontWeight="bold">
                            {subCategory.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={subCategory.parentCategoryName || 'Unknown'} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {subCategory.sortOrder || 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {subCategory.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit Sub-Category">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => openSubCategoryEdit(subCategory)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Sub-Category">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => setDeleteDialog({ 
                              open: true, 
                              type: 'sub-category', 
                              item: subCategory 
                            })}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Category Modal */}
      <Dialog 
        open={categoryModal.open} 
        onClose={() => setCategoryModal({ 
          open: false, 
          mode: 'add', 
          data: { id: '', name: '', description: '', sortOrder: 1, image: '', categoryId: '' } 
        })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {categoryModal.mode === 'add' ? 'Add New Category' : 'Edit Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={categoryModal.data.name}
              onChange={(e) => setCategoryModal(prev => ({
                ...prev,
                data: { ...prev.data, name: e.target.value }
              }))}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Category ID (URL Parameter)"
              value={categoryModal.data.categoryId}
              onChange={(e) => setCategoryModal(prev => ({
                ...prev,
                data: { ...prev.data, categoryId: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }
              }))}
              helperText="Used for URL-friendly category links (e.g., 'fresh-chicken')"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={categoryModal.data.description}
              onChange={(e) => setCategoryModal(prev => ({
                ...prev,
                data: { ...prev.data, description: e.target.value }
              }))}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Sort Order"
              type="number"
              value={categoryModal.data.sortOrder}
              onChange={(e) => setCategoryModal(prev => ({
                ...prev,
                data: { ...prev.data, sortOrder: parseInt(e.target.value) || 1 }
              }))}
              inputProps={{ min: 1 }}
              sx={{ mb: 2 }}
            />
            <ImageUpload
              images={categoryModal.data.image ? [categoryModal.data.image] : []}
              onImagesChange={(images) => setCategoryModal(prev => ({
                ...prev,
                data: { ...prev.data, image: images.length > 0 ? images[0] : '' }
              }))}
              uploadPath="categories"
              maxImages={1}
              label="Category Image"
              helperText="Upload a category image (optional)"
              multiple={false}
              showPreviews={true}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCategoryModal({ 
              open: false, 
              mode: 'add', 
              data: { id: '', name: '', description: '', sortOrder: 1, image: '', categoryId: '' } 
            })}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCategorySubmit}
            variant="contained"
            disabled={!isCategoryFormValid()}
          >
            {categoryModal.mode === 'add' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sub-Category Modal */}
      <Dialog 
        open={subCategoryModal.open} 
        onClose={() => setSubCategoryModal({ 
          open: false, 
          mode: 'add', 
          data: { id: '', name: '', description: '', parentCategoryId: '', sortOrder: 1 } 
        })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {subCategoryModal.mode === 'add' ? 'Add New Sub-Category' : 'Edit Sub-Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Sub-Category Name"
              value={subCategoryModal.data.name}
              onChange={(e) => setSubCategoryModal(prev => ({
                ...prev,
                data: { ...prev.data, name: e.target.value }
              }))}
              required
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel>Parent Category</InputLabel>
              <Select
                value={subCategoryModal.data.parentCategoryId}
                onChange={(e) => setSubCategoryModal(prev => ({
                  ...prev,
                  data: { ...prev.data, parentCategoryId: e.target.value }
                }))}
                label="Parent Category"
              >
                {categories.map((category) => (
                  <MenuItem key={category.documentId} value={category.documentId}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Description"
              value={subCategoryModal.data.description}
              onChange={(e) => setSubCategoryModal(prev => ({
                ...prev,
                data: { ...prev.data, description: e.target.value }
              }))}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Sort Order"
              type="number"
              value={subCategoryModal.data.sortOrder}
              onChange={(e) => setSubCategoryModal(prev => ({
                ...prev,
                data: { ...prev.data, sortOrder: e.target.value }
              }))}
              inputProps={{ min: 1 }}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSubCategoryModal({ 
              open: false, 
              mode: 'add', 
              data: { id: '', name: '', description: '', parentCategoryId: '', sortOrder: 1 } 
            })}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubCategorySubmit}
            variant="contained"
            disabled={!isSubCategoryFormValid()}
          >
            {subCategoryModal.mode === 'add' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: '', item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete {deleteDialog.type}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.item?.name}"?
          </Typography>
          {deleteDialog.type === 'category' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Note: You cannot delete a category that has sub-categories.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, type: '', item: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManagement; 